import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, TrashIcon, EnvelopeIcon, CreditCardIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { ManualPayment } from '../../types/supabase';
import Notification from '../Notification';

interface Student {
  id: string;
  name: string;
  email: string | null;
  stripe_customer_id?: string | null;
  billing_mode?: string | null;
  billing_guardian_id?: string | null;
}

interface StripePayment {
  id: string;
  date: string;
  amount: number;
  amount_refunded: number;
  description: string;
  status: 'paid' | 'refunded' | 'partially_refunded';
  source_type: 'invoice' | 'charge';
  receipt_url: string | null;
}

type SourceFilter = 'all' | 'manual' | 'stripe';

interface PaymentHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: Student | null;
  getAuthToken: () => string | null;
}

export default function PaymentHistoryModal({
  isOpen,
  onClose,
  student,
  getAuthToken,
}: PaymentHistoryModalProps) {
  const [payments, setPayments] = useState<ManualPayment[]>([]);
  const [stripePayments, setStripePayments] = useState<StripePayment[]>([]);
  const [stripeBillingContext, setStripeBillingContext] = useState<{
    family_billed: boolean;
    billing_primary_name?: string | null;
    filtered_to_student?: boolean;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingStripe, setIsLoadingStripe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [manualDisplayLimit, setManualDisplayLimit] = useState(10);
  const [stripeDisplayLimit, setStripeDisplayLimit] = useState(10);
  const [sendingEmail, setSendingEmail] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title: string;
    type?: 'success' | 'error' | 'general';
  }>({ show: false, message: '', title: '', type: 'success' });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const fetchManualPayments = useCallback(async () => {
    if (!student) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${backendUrl}/api/manual-payments/student/${student.id}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!response.ok) throw new Error('Failed to fetch payment history');
      const data = await response.json();
      setPayments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch payment history');
    } finally {
      setIsLoading(false);
    }
  }, [student, backendUrl, getAuthToken]);

  const isFamilyBilledMember =
    student?.billing_mode === 'family' && !!student?.billing_guardian_id;

  const fetchStripePayments = useCallback(async () => {
    if (!student?.id) return;
    if (!student.stripe_customer_id && !isFamilyBilledMember) return;
    setIsLoadingStripe(true);
    setStripeError(null);
    try {
      const response = await fetch(
        `${backendUrl}/api/stripe/students/${student.id}/stripe-payment-history`,
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      if (!response.ok) throw new Error('Failed to fetch Stripe transactions');
      const data = await response.json();
      setStripePayments(Array.isArray(data.payments) ? data.payments : []);
      setStripeBillingContext(data.billing_context ?? null);
    } catch (err) {
      setStripeError(err instanceof Error ? err.message : 'Failed to fetch Stripe transactions');
      setStripeBillingContext(null);
    } finally {
      setIsLoadingStripe(false);
    }
  }, [student, isFamilyBilledMember, backendUrl, getAuthToken]);

  useEffect(() => {
    if (isOpen && student) {
      fetchManualPayments();
      fetchStripePayments();
    } else if (!isOpen) {
      setStripeBillingContext(null);
    }
  }, [isOpen, student, fetchManualPayments, fetchStripePayments]);

  const handleDeleteClick = (paymentIds: string | string[]) => {
    setPaymentToDelete(Array.isArray(paymentIds) ? paymentIds.join(',') : paymentIds);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!paymentToDelete) return;
    setIsDeleting(true);
    try {
      const idsToDelete = paymentToDelete.split(',');
      const deletePromises = idsToDelete.map((id) =>
        fetch(`${backendUrl}/api/manual-payments/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        })
      );
      const responses = await Promise.all(deletePromises);
      if (responses.some((r) => !r.ok)) throw new Error('Failed to delete one or more payments');
      await fetchManualPayments();
      setShowDeleteConfirm(false);
      setPaymentToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete payment');
      setShowDeleteConfirm(false);
      setPaymentToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setPaymentToDelete(null);
  };

  const handleSendEmailReceipt = async (paymentGroup: ManualPayment[]) => {
    if (!student || !student.email) {
      setError('Student does not have an email address on file');
      return;
    }
    const groupKey = paymentGroup.map((p) => p.id).join(',');
    setSendingEmail(groupKey);
    setError(null);
    try {
      const response = await fetch(`${backendUrl}/api/manual-payments/send-receipt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
        body: JSON.stringify({
          payment_ids: paymentGroup.map((p) => p.id),
          student_id: student.id,
          student_email: student.email,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send email receipt');
      }
      setNotification({
        show: true,
        title: 'Email Sent',
        message: `Receipt sent to ${student.email}`,
        type: 'success',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send email receipt');
    } finally {
      setSendingEmail(null);
    }
  };

  const isInDateRange = (dateStr: string) => {
    if (!dateFrom && !dateTo) return true;
    const d = new Date(dateStr);
    if (dateFrom && dateTo) return d >= new Date(dateFrom) && d <= new Date(dateTo);
    if (dateFrom) return d >= new Date(dateFrom);
    if (dateTo) return d <= new Date(dateTo);
    return true;
  };

  const filteredManual = payments.filter((p) => isInDateRange(p.payment_date));
  const filteredStripe = stripePayments.filter((p) => isInDateRange(p.date));

  const groupPayments = (list: ManualPayment[]) => {
    const groups: { key: string; payments: ManualPayment[] }[] = [];
    list.forEach((payment) => {
      const key = `${payment.payment_date}-${payment.amount}-${payment.period_start}-${payment.period_end}-${payment.notes || 'none'}-${payment.payment_method}`;
      const existing = groups.find((g) => g.key === key);
      if (existing) existing.payments.push(payment);
      else groups.push({ key, payments: [payment] });
    });
    return groups;
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const getPaymentMethodLabel = (method: string) => {
    const labels: Record<string, string> = {
      cash: 'Cash', check: 'Check', apple_pay: 'Apple Pay', google_pay: 'Google Pay',
      venmo: 'Venmo', paypal: 'PayPal', cashapp: 'Cash App', zelle: 'Zelle',
      bank_transfer: 'Bank Transfer', other: 'Other', credit: 'Account Credit',
    };
    return labels[method] || method;
  };

  const totalManualAmount = filteredManual.reduce((sum, p) => sum + Number(p.amount), 0);
  const totalStripeAmount = filteredStripe.reduce((sum, p) => sum + Number(p.amount), 0);
  const grandTotal =
    sourceFilter === 'manual'
      ? totalManualAmount
      : sourceFilter === 'stripe'
      ? totalStripeAmount
      : totalManualAmount + totalStripeAmount;

  const latestDate = (() => {
    const dates: string[] = [];
    if (sourceFilter !== 'stripe' && filteredManual.length > 0) dates.push(filteredManual[0].payment_date);
    if (sourceFilter !== 'manual' && filteredStripe.length > 0) dates.push(filteredStripe[0].date);
    if (dates.length === 0) return null;
    return dates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
  })();

  const hasStripe = !!student?.stripe_customer_id || isFamilyBilledMember;
  const showStripeTab = hasStripe;

  if (!isOpen || !student) return null;

  const groupedManual = groupPayments(filteredManual);

  return (
    <>
      <Notification
        show={notification.show}
        message={notification.message}
        title={notification.title}
        type={notification.type}
        onClose={() => setNotification({ show: false, message: '', title: '', type: 'success' })}
      />
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Payment History</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{student.name}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {stripeBillingContext?.family_billed &&
              stripeBillingContext.filtered_to_student && (
                <div className="mb-4 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-800 dark:border-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-200">
                  Stripe payments are pulled from{" "}
                  {stripeBillingContext.billing_primary_name || "the billing primary"}
                  &apos;s family invoices, filtered to this student&apos;s memberships.
                </div>
              )}
            {/* Source tabs */}
            {showStripeTab && (
              <div className="mb-4 flex gap-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-1 w-fit">
                {(['all', 'manual', 'stripe'] as SourceFilter[]).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => { setSourceFilter(tab); setManualDisplayLimit(10); setStripeDisplayLimit(10); }}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      sourceFilter === tab
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    {tab === 'all' && 'All'}
                    {tab === 'manual' && (
                      <>
                        <BanknotesIcon className="h-4 w-4" />
                        Cash / Manual
                      </>
                    )}
                    {tab === 'stripe' && (
                      <>
                        <CreditCardIcon className="h-4 w-4" />
                        Stripe
                      </>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Date filter */}
            <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From Date</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => { setDateFrom(e.target.value); setManualDisplayLimit(10); setStripeDisplayLimit(10); }}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To Date</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => { setDateTo(e.target.value); setManualDisplayLimit(10); setStripeDisplayLimit(10); }}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                  />
                </div>
                {(dateFrom || dateTo) && (
                  <button
                    onClick={() => { setDateFrom(''); setDateTo(''); setManualDisplayLimit(10); setStripeDisplayLimit(10); }}
                    className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Loading states */}
            {(isLoading || isLoadingStripe) && (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading payment history…</p>
              </div>
            )}

            {/* Errors */}
            {!isLoading && !isLoadingStripe && (error || stripeError) && (
              <div className="mb-4 space-y-2">
                {error && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  </div>
                )}
                {stripeError && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                    <p className="text-sm text-yellow-700 dark:text-yellow-400">Stripe: {stripeError}</p>
                  </div>
                )}
              </div>
            )}

            {!isLoading && !isLoadingStripe && (
              <div className="space-y-4">
                {/* Summary card */}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {dateFrom || dateTo ? 'Filtered' : 'Total'} Payments
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {sourceFilter === 'all' && filteredManual.length + filteredStripe.length}
                        {sourceFilter === 'manual' && filteredManual.length}
                        {sourceFilter === 'stripe' && filteredStripe.length}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {dateFrom || dateTo ? 'Filtered' : 'Total'} Paid
                      </p>
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(grandTotal)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Latest Payment</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {latestDate ? formatDate(latestDate) : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Manual payments section */}
                {sourceFilter !== 'stripe' && (
                  <div>
                    {showStripeTab && (
                      <div className="flex items-center gap-2 mb-2">
                        <BanknotesIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Cash / Manual Payments
                        </span>
                      </div>
                    )}
                    {filteredManual.length === 0 ? (
                      <div className="text-center py-6 rounded-lg border border-dashed border-gray-200 dark:border-gray-700">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {dateFrom || dateTo ? 'No cash payments in this date range' : 'No cash / manual payments on record'}
                        </p>
                      </div>
                    ) : (
                      <>
                      <div className="space-y-3">
                        {groupedManual.slice(0, manualDisplayLimit).map((group) => {
                          const firstPayment = group.payments[0];
                          const isGrouped = group.payments.length > 1;
                          let membershipNames = '';
                          let productName = '';
                          let creditApplied = 0;
                          let actualNotes = firstPayment.notes || '';
                          if (firstPayment.notes?.startsWith('__MEMBERSHIPS__:')) {
                            const parts = firstPayment.notes.split('\n\n');
                            membershipNames = parts[0].replace('__MEMBERSHIPS__:', '');
                            actualNotes = parts.slice(1).join('\n\n');
                          } else if (firstPayment.notes?.startsWith('__PRODUCT__:')) {
                            const parts = firstPayment.notes.split('\n\n');
                            // The first chunk can have multiple single-newline lines:
                            // __PRODUCT__:Product Name\n__CREDIT_APPLIED__:25.00
                            const firstChunkLines = parts[0].split('\n');
                            productName = firstChunkLines[0].replace('__PRODUCT__:', '');
                            const creditLine = firstChunkLines.find((l) => l.startsWith('__CREDIT_APPLIED__:'));
                            if (creditLine) {
                              creditApplied = parseFloat(creditLine.replace('__CREDIT_APPLIED__:', '')) || 0;
                            }
                            actualNotes = parts.slice(1).join('\n\n');
                          }
                          return (
                            <div
                              key={group.key}
                              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                      {formatCurrency(Number(firstPayment.amount))}
                                    </span>
                                    <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                                      {getPaymentMethodLabel(firstPayment.payment_method)}
                                    </span>
                                    {isGrouped && (
                                      <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded">
                                        {group.payments.length} memberships
                                      </span>
                                    )}
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                      <span className="text-gray-500 dark:text-gray-400">Payment Date:</span>
                                      <span className="ml-2 text-gray-900 dark:text-white">{formatDate(firstPayment.payment_date)}</span>
                                    </div>
                                    {!productName && (
                                      <div>
                                        <span className="text-gray-500 dark:text-gray-400">Period:</span>
                                        <span className="ml-2 text-gray-900 dark:text-white">
                                          {formatDate(firstPayment.period_start)} – {formatDate(firstPayment.period_end)}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  {productName ? (
                                    <>
                                      <div className="mt-2 text-sm">
                                        <span className="text-gray-500 dark:text-gray-400">Product:</span>
                                        <span className="ml-2 text-gray-900 dark:text-white">{productName}</span>
                                      </div>
                                      {creditApplied > 0 && (
                                        <div className="mt-1 text-sm flex items-center gap-1">
                                          <span className="text-gray-500 dark:text-gray-400">Credit applied:</span>
                                          <span className="ml-1 font-medium text-emerald-600 dark:text-emerald-400">
                                            −${creditApplied.toFixed(2)}
                                          </span>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div className="mt-2 text-sm">
                                      <span className="text-gray-500 dark:text-gray-400">
                                        {membershipNames || isGrouped ? 'Memberships:' : 'Membership:'}
                                      </span>
                                      {membershipNames ? (
                                        <span className="ml-2 text-gray-900 dark:text-white">{membershipNames}</span>
                                      ) : (
                                        <div className="ml-2 space-y-0.5">
                                          {group.payments.map((p) =>
                                            p.memberships ? (
                                              <div key={p.id} className="text-gray-900 dark:text-white">
                                                • {p.memberships.name} (${p.memberships.price})
                                              </div>
                                            ) : null
                                          )}
                                          {!group.payments.some((p) => p.memberships) && (
                                            <span className="text-gray-900 dark:text-white">General payment (no membership)</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {actualNotes && (
                                    <div className="mt-2 text-sm">
                                      <span className="text-gray-500 dark:text-gray-400">Notes:</span>
                                      <span className="ml-2 text-gray-700 dark:text-gray-300 italic">{actualNotes}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 ml-4">
                                  <button
                                    onClick={() => handleSendEmailReceipt(group.payments)}
                                    disabled={!student.email || sendingEmail === group.key}
                                    className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={student.email ? 'Send email receipt' : 'Student has no email on file'}
                                  >
                                    {sendingEmail === group.key ? (
                                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                                    ) : (
                                      <EnvelopeIcon className="h-5 w-5" />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClick(group.payments.map((p) => p.id))}
                                    className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                    title={isGrouped ? 'Delete all grouped payments' : 'Delete payment'}
                                  >
                                    <TrashIcon className="h-5 w-5" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {groupedManual.length > manualDisplayLimit && (
                        <button
                          type="button"
                          onClick={() => setManualDisplayLimit((l) => l + 10)}
                          className="mt-3 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          Load more ({groupedManual.length - manualDisplayLimit} remaining)
                        </button>
                      )}
                      </>
                    )}
                  </div>
                )}

                {/* Stripe payments section */}
                {sourceFilter !== 'manual' && showStripeTab && (
                  <div>
                    <div className="flex items-center gap-2 mb-2 mt-4">
                      <CreditCardIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Stripe Transactions
                      </span>
                    </div>
                    {filteredStripe.length === 0 ? (
                      <div className="text-center py-6 rounded-lg border border-dashed border-gray-200 dark:border-gray-700">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {dateFrom || dateTo
                            ? 'No Stripe transactions in this date range'
                            : 'No Stripe transactions on record'}
                        </p>
                      </div>
                    ) : (
                      <>
                      <div className="space-y-3">
                        {filteredStripe.slice(0, stripeDisplayLimit).map((sp) => (
                          <div
                            key={sp.id}
                            className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {formatCurrency(sp.amount)}
                                  </span>
                                  <span className="px-2 py-0.5 text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 rounded">
                                    Stripe
                                  </span>
                                  {sp.status === 'paid' && (
                                    <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded">
                                      Paid
                                    </span>
                                  )}
                                  {sp.status === 'refunded' && (
                                    <span className="px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded">
                                      Refunded
                                    </span>
                                  )}
                                  {sp.status === 'partially_refunded' && (
                                    <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 rounded">
                                      Partial Refund
                                    </span>
                                  )}
                                  {sp.source_type === 'invoice' && (
                                    <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                                      Invoice
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm">
                                  <span className="text-gray-500 dark:text-gray-400">Date:</span>
                                  <span className="ml-2 text-gray-900 dark:text-white">{formatDate(sp.date)}</span>
                                </div>
                                {sp.description && (
                                  <div className="mt-1 text-sm">
                                    <span className="text-gray-500 dark:text-gray-400">Description:</span>
                                    <span className="ml-2 text-gray-700 dark:text-gray-300">{sp.description}</span>
                                  </div>
                                )}
                                {sp.amount_refunded > 0 && (
                                  <div className="mt-1 text-sm">
                                    <span className="text-gray-500 dark:text-gray-400">Refunded:</span>
                                    <span className="ml-2 text-red-600 dark:text-red-400 font-medium">
                                      {formatCurrency(sp.amount_refunded)}
                                      {sp.status === 'partially_refunded' && (
                                        <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                                          (net {formatCurrency(sp.amount - sp.amount_refunded)})
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                )}
                              </div>
                              {sp.receipt_url && (
                                <a
                                  href={sp.receipt_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-4 flex-shrink-0 p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                  title="View Stripe receipt"
                                >
                                  <EnvelopeIcon className="h-5 w-5" />
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      {filteredStripe.length > stripeDisplayLimit && (
                        <button
                          type="button"
                          onClick={() => setStripeDisplayLimit((l) => l + 10)}
                          className="mt-3 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          Load more ({filteredStripe.length - stripeDisplayLimit} remaining)
                        </button>
                      )}
                      </>
                    )}
                  </div>
                )}

                {/* Empty state when truly nothing */}
                {sourceFilter === 'all' &&
                  filteredManual.length === 0 &&
                  filteredStripe.length === 0 &&
                  !isLoading &&
                  !isLoadingStripe && (
                    <div className="text-center py-8">
                      <p className="text-gray-500 dark:text-gray-400">No payment history found</p>
                    </div>
                  )}
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 p-6">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>

        {/* Delete confirmation */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Delete Payment Record{paymentToDelete?.includes(',') ? 's' : ''}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                {paymentToDelete?.includes(',')
                  ? `Are you sure you want to delete these ${paymentToDelete.split(',').length} grouped payment records? This action cannot be undone.`
                  : 'Are you sure you want to delete this payment record? This action cannot be undone.'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={cancelDelete}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
